/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/build/QaPhoneBuildCard.tsx
 * FILE_ID: JOBHUB-QA-PHONE-BUILD-CARD-001
 * PURPOSE: One editable check as a card, for Build mode on a phone.
 * WHY: The table needs ~640px of column width. On a 360px screen it becomes a
 *      horizontal scroll, so every field but the first is off-screen. The card
 *      stacks the same five fields the table row edits and commits through the
 *      same editor, so the two views cannot drift.
 * LANDMINE: everything here is in NORMAL FLOW. The expanded body pushes the
 *      cards below it down; it must never overlay a neighbour (rule 06 §2).
 *      The one exception is the product suggestion list, which is a true
 *      anchored popover.
 * RELATED:
 * - ../../../../qa/useQaChecklistEditor.ts (every write)
 * - ../../../../qa/QaChecklistRow.tsx (the desktop presentation of this data)
 */
import { ChevronDown, CornerDownRight, Loader2, MoreVertical, Plus } from 'lucide-react'
import { QaDragHandle } from '../../../../qa/QaDragHandle'
import { useQaHoldTimer } from '../../../../qa/qaHoldTimer'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { EditableCell } from '../../../../qa/EditableCell'
import { QaProductSearchField } from '../../../../qa/QaProductSearchField'
import { joinProductNames, resolvedProductNames } from '../../../../qa/qaProductResolve'
import type { QaItemEdit, QaItemInstance } from '../../../../qa/qaApi'
import type { JobHubHttpClient } from '../../../../jobHubDataAdapter'
import { QA_PHONE_CONTROLS } from '../qaPhoneControlInventory'

export function QaPhoneBuildCard({
  item,
  depth,
  expanded,
  disabled,
  busy,
  client,
  selected,
  onToggleSelected,
  onToggleExpanded,
  onEdit,
  onRowActions,
  onLongPress,
  onAddSubRow,
  areaNames = [],
  checklistNames = [],
}: {
  item: QaItemInstance
  depth: number
  expanded: boolean
  disabled: boolean
  busy: boolean
  client?: JobHubHttpClient
  selected: boolean
  onToggleSelected: () => void
  onToggleExpanded: () => void
  onEdit: (item: QaItemInstance, fields: QaItemEdit) => void
  onRowActions: () => void
  onLongPress: () => void
  onAddSubRow: () => void
  areaNames?: string[]
  checklistNames?: string[]
}) {
  const hold = useQaHoldTimer(() => onLongPress())
  const label = `${item.item_number} ${item.title}`
  const productDisplay = joinProductNames(resolvedProductNames({
    itemNames: item.product_names,
    itemProduct: item.product,
    checklistNames,
    areaNames,
  }))
  return (
    <li
      data-qa-item-row={item.id}
      data-qa-drop={`row:${item.id}`}
      style={{ marginLeft: Math.min(depth, 3) * 12 }}
      {...hold}
      onContextMenu={(event) => {
        event.preventDefault()
        onLongPress()
      }}
      className={cn(
        'rounded-xl border bg-card',
        selected ? 'border-primary ring-1 ring-primary/30' : 'border-border',
      )}
    >
      <div className="flex items-start gap-2 p-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          aria-label={`Select ${label}`}
          data-testid={`qa-item-select-${item.id}`}
          className="mt-2.5 h-3.5 w-3.5 shrink-0 rounded border-border"
        />
        <QaDragHandle kind="row" id={item.id} label={item.title || 'step'} disabled={disabled} />
        {depth > 0 && (
          <CornerDownRight className="mt-2.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
        )}
        <span className="mt-2 shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {item.display_number ?? item.item_number}
        </span>
        <div className="min-w-0 flex-1">
          <EditableCell
            value={item.title}
            singleLine
            allowEmpty={false}
            disabled={disabled}
            ariaLabel={`Item ${item.item_number} title`}
            placeholder={depth > 0 ? 'Sub-check name' : 'Check name'}
            onCommit={(title) => onEdit(item, { title })}
          />
          {item.assigned_to_name ? (
            <span className="block truncate text-[11px] text-muted-foreground">
              {item.assigned_to_name}{item.due_date ? ` · ${item.due_date}` : ''}
            </span>
          ) : null}
        </div>
        {busy && <Loader2 className="mt-2.5 h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />}
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} details for ${label}`}
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onRowActions}
          data-qa-phone-control={QA_PHONE_CONTROLS.rowActions}
          aria-label={`Row actions for ${label}`}
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground"
        >
          <MoreVertical className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 border-t border-border/60 px-2 pb-2 pt-2">
          <Field label="Product">
            <QaProductSearchField
              value={productDisplay}
              client={client}
              disabled={disabled}
              ariaLabel={`Item ${item.item_number} product`}
              onCommit={(product) => {
                const ownNames = item.product_names ?? []
                const ownProduct = (item.product ?? '').trim()
                if (ownNames.length === 0 && !ownProduct && product === productDisplay) return
                onEdit(item, { product })
              }}
            />
          </Field>
          <Field label="Requirement">
            <EditableCell
              value={item.requirement_text ?? ''}
              disabled={disabled}
              ariaLabel={`Item ${item.item_number} requirement`}
              placeholder="What to verify"
              onCommit={(requirement_text) => onEdit(item, { requirement_text })}
            />
          </Field>
          <Field label="Acceptance criteria">
            <EditableCell
              value={item.acceptance_criteria ?? ''}
              disabled={disabled}
              ariaLabel={`Item ${item.item_number} acceptance criteria`}
              placeholder="Pass condition"
              onCommit={(acceptance_criteria) => onEdit(item, { acceptance_criteria })}
            />
          </Field>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
            <label className="flex min-h-[44px] items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={!!item.has_hold_point}
                disabled={disabled}
                data-qa-phone-control={QA_PHONE_CONTROLS.hold}
                aria-label={`Park item ${item.item_number}`}
                onChange={(e) => onEdit(item, { has_hold_point: e.target.checked })}
                className="h-4 w-4 rounded border-border text-amber-600 focus:ring-amber-500"
              />
              Hold point
            </label>
            <button
              type="button"
              onClick={onAddSubRow}
              disabled={disabled}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 text-xs font-medium text-muted-foreground disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Sub-check
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <span className="block px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  )
}
